import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { initMatomo, trackEvent } from "./matomo";
import { AUTH_CHANGED_EVENT, hasSession, clearIdentity } from "./utils/authEvents";
import { ChatNotifProvider } from "./contexts/ChatNotifContext";
import { FavoritesProvider } from "./contexts/FavoritesContext";
import "./App.css";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import Home from "./pages/Home";
import Modallogin from "./components/auth/Modallogin";
import { Box, Flex, HStack, Skeleton } from "@chakra-ui/react";
import MaintenancePage from "./components/common/MaintenancePage";
import ScrollToTop from "./components/common/ScrollToTop";
import MatomoPageViews from "./components/common/MatomoPageViews";

const MyChat = lazy(() => import("./features/chat/MyChat"));
const CityPage = lazy(() => import("./pages/CityPage"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Contact = lazy(() => import("./pages/Contact"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const NotFound = lazy(() => import("./components/common/NotFound"));
const PreviewAd = lazy(() => import("./features/ads/PreviewAd"));
const CatagoryView = lazy(() => import("./features/ads/CatagoryView"));
const Myads = lazy(() => import("./features/ads/Myads"));
const SellForm = lazy(() => import("./features/sell/SellForm"));
const EditAd = lazy(() => import("./features/sell/EditAd"));
const AdSuccess = lazy(() => import("./features/sell/AdSuccess"));
const UserProfileEdit = lazy(() => import("./features/profile/UserProfileEdit"));
const Profile = lazy(() => import("./features/profile/Profile"));
const SearchProfile = lazy(() => import("./features/profile/SearchProfile"));
const SearchResults = lazy(() => import("./features/search/SearchResults"));
const AdminDashboard = lazy(() => import("./features/admin/AdminDashboard"));
const Favorites = lazy(() => import("./pages/Favorites"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const ProductOrCategoryRoute = lazy(() => import("./components/common/ProductOrCategoryRoute"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const BrandsIndex = lazy(() => import("./pages/BrandsIndex"));


function App() {
  // for modal 
  const [staticModal, setStaticModal] = useState(true);
  const toggleShow = () => setStaticModal(!staticModal);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [auth, setAuth] = useState(false);
  const [loading, setLoading] = useState(true); // Initial loading state
  const [backendStatus, setBackendStatus] = useState('online'); // Track backend status


  async function checkBackendStatus() {
    try {
      const response = await fetch(`${backendUrl}/api/check-status`);
      const data = await response.json();
      setBackendStatus(data.status);
    } catch (error) {
      setBackendStatus('offline');
    }
    // Note: setLoading is NOT called here — we wait for both checks to finish
  }

  async function checkAuthentication() {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${backendUrl}/auth-endpoint`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.isAuthenticated) {
          setAuth(true);
          if (data.username && !localStorage.getItem('authusername')) {
            localStorage.setItem('authusername', data.username);
          }
          return;
        }
      }
      // Jeton invalide ou expiré. On efface TOUTE l'identité, pas seulement le
      // jeton : `authname` et `authpicture` restaient sinon en place et la
      // navbar continuait d'afficher l'utilisateur comme connecté.
      clearIdentity();
      setAuth(false);
    } catch (error) {
      console.error(error);
    }
  }

  // L'identité peut être effacée hors de React — par l'intercepteur axios,
  // quand une requête revient en 401 parce que la session a expiré en cours de
  // navigation. Sans cette écoute, `auth` resterait à true et la navbar
  // continuerait d'afficher un état connecté trompeur jusqu'au rechargement.
  useEffect(() => {
    const sync = () => setAuth(hasSession());
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, sync);
  }, []);

  useEffect(() => {
    // Matomo auto-hébergé. Le code chargeait auparavant, en dur, le conteneur
    // Matomo Cloud : depuis la migration il ne répond plus, d'où l'arrêt du
    // suivi. Les variables sont injectées au BUILD par Railway — un CRA fige
    // ses REACT_APP_* à la compilation, elles ne peuvent pas être lues à
    // l'exécution.
    initMatomo();

    // Device type — après l'init, sinon `_paq` n'existe pas encore
    trackEvent('Session', 'device', window.innerWidth <= 768 ? 'mobile' : 'desktop');

    // Run both checks in parallel; only hide the loading screen when BOTH finish
    Promise.all([checkBackendStatus(), checkAuthentication()])
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return (
    <Box bg="white" minH="100vh">
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100"
        boxShadow="sm" px={4} h="60px"
        display="flex" alignItems="center" justifyContent="space-between">
        <Skeleton h="28px" w="110px" borderRadius="md" startColor="red.100" endColor="red.50" />
        <HStack spacing={2}>
          <Skeleton h="28px" w="80px" borderRadius="md" />
          <Skeleton h="28px" w="80px" borderRadius="md" />
        </HStack>
      </Box>
      <Box bg="#F5F5F5" minH="calc(100vh - 60px)" />
    </Box>
  );

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <MatomoPageViews />
      <ChatNotifProvider>
      <FavoritesProvider>
      <div className="App">

        {/* USE NAVBAR */}
        <Navbar auth={auth} setAuth={setAuth} />

        {/* routes */}
        <main>
          {backendStatus === 'online' ? (
            <Suspense fallback={<Box bg="#F5F5F5" minH="100vh" />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/adsuccess" element={<AdSuccess />} />
                <Route path="/article/:id/:slug" element={<PreviewAd auth={auth} />} />
                <Route path="/article/:id" element={<PreviewAd auth={auth} />} />
                {/* legacy redirect support */}
                <Route path="/preview_ad/:id" element={<PreviewAd auth={auth} />} />
                {/* ouedkniss-style single-segment URL — handles both products
                    (slug ends with -d{numericId}) and category pages */}
                {auth === true && <Route path="/sell" element={<SellForm />} />}
                {auth === true && <Route path="/attributes/:category/:item" element={<SellForm />} />}
                {auth === true && <Route path="/chat/:id/:username" element={<MyChat />} />}
                {auth === true && <Route path="/chat" element={<MyChat />} />}
                {auth === true && <Route path="/editprofile" element={<UserProfileEdit />} />}
                {auth === true && <Route path="/profile" element={<Profile />} />}
                {auth === true && <Route path="/myads" element={<Myads />} />}
                {auth === true && <Route path="/editad/:id" element={<EditAd />} />}
                {auth === true && <Route path="/admin" element={<AdminDashboard />} />}
                {auth === true && <Route path="/favorites" element={<Favorites />} />}
                {auth === false && <Route path="/sell" element={[<Modallogin setStaticModal={setStaticModal} toggleShow={toggleShow} staticModal={staticModal} />, <Home />]} />}
                {auth === false && <Route path="/attributes/:category/:item" element={[<Modallogin setStaticModal={setStaticModal} toggleShow={toggleShow} staticModal={staticModal} />, <Home />]} />}
                <Route path="/marque/:slug" element={<BrandPage />} />
                <Route path="/marques" element={<BrandsIndex />} />
                <Route path="/:slug" element={<ProductOrCategoryRoute auth={auth} />} />
                <Route path="/results" element={<SearchResults />} />
                <Route path="/profile/:username" element={<SearchProfile />} />
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/comment-ca-marche" element={<HowItWorks />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogArticle />} />
                <Route path="/ville/:city" element={<CityPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          ) : (
            <MaintenancePage />
          )}
        </main>

        {/* USE FOOTER */}
        <Footer />

      </div>
      </FavoritesProvider>
      </ChatNotifProvider>
    </Router>
  );
}

export default App;